import { MESSAGE } from '@/constants/message.constant';
import { createAppSlice } from '../createAppSlice';
import { transformerArray } from '../transformer';
import { MessageType } from '@/types/common';
import { Message } from '@/enums/message.enum';
import { HISTORY_TYPE } from '@/constants/type';
import { PAGINATION } from '@/constants/pagination.constant';

type NutritionStandardSliceState = {
  loading: boolean;
  listAgeNutritionStandard: NutritionAge[];
  listHealthStatusNutritionStandard: NutritionHeathStatus[];
  listGenderNutritionStandard: NutritionGender[];
  listPhysicalActivityNutritionStandard: NutritionPhysicalActivity[];
  listHealthStatusZCoreNutritionStandard: NutritionHealthStatusZCore60[];
  listBreastMilkLevel: BreastMilkLevel[];
  filteredNutritionalStandards: FilterNutritionStandard[];
  listHistory: NutritionHistory[];
  paginationHistory: Pagination;
  pagination: Pagination;
  message?: MessageType;
  downloadTemplateNutritionStandard?: TemplateNutritionStandard;
};

const initialState: NutritionStandardSliceState = {
  loading: false,
  listAgeNutritionStandard: [],
  listHealthStatusNutritionStandard: [],
  listGenderNutritionStandard: [],
  listPhysicalActivityNutritionStandard: [],
  listHealthStatusZCoreNutritionStandard: [],
  listBreastMilkLevel: [],
  listHistory: [],
  filteredNutritionalStandards: [],
  paginationHistory: PAGINATION,
  pagination: PAGINATION,
  downloadTemplateNutritionStandard: undefined
};

export const nutritionStandardSlice = createAppSlice({
  name: 'nutritionStandard',
  initialState,
  reducers: (create) => ({
    getListAgeNutritionStandard: create.asyncThunk(
      async (type?: string) => {
        const response = await apiFetcher.get(
          `age-ranges?type=${type ?? 'NUTRITIONAL_STANDARD'}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listAgeNutritionStandard = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listAgeNutritionStandard = [];
        }
      }
    ),
    getListHealthStatusNutritionStandard: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('nutrition-health-statuses');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHealthStatusNutritionStandard = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listHealthStatusNutritionStandard = [];
        }
      }
    ),
    getListGenderNutritionStandard: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('gender');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listGenderNutritionStandard = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listGenderNutritionStandard = [];
        }
      }
    ),
    getListPhysicalActivityNutritionStandard: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('option/get-list-by-type');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivityNutritionStandard = transformerArray(
            action.payload
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listPhysicalActivityNutritionStandard = [];
        }
      }
    ),
    getListHealthStatusZCoreNutritionStandard: create.asyncThunk(
      async ({ type, option }: { type: string; option?: string }) => {
        const response = await apiFetcher.get(
          `health-status-bmi?type=${type}&option=${option}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHealthStatusZCoreNutritionStandard = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listHealthStatusZCoreNutritionStandard = [];
        }
      }
    ),
    editNutritionStandard: create.asyncThunk(
      async ({ id, payload }: { id: number; payload: any }) => {
        const response = await apiFetcher.put(
          `nutritional-standards/update/${id}`,
          payload
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_NUTRITION_STANDARD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_NUTRITION_STANDARD_FAIL
          };
        }
      }
    ),
    createNutritionStandard: create.asyncThunk(
      async ({ payload }: { payload: any }) => {
        const response = await apiFetcher.post(
          `nutritional-standards/create`,
          payload
        );
        return response.message;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: action.payload || MESSAGE.CREATE_NUTRITION_STANDARD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_NUTRITION_STANDARD_FAIL
          };
        }
      }
    ),
    deleteNutritionStandard: create.asyncThunk(
      async ({ id }: { id: number }) => {
        const response = await apiFetcher.delete(
          `nutritional-standards/delete/${id}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_NUTRITION_STANDARD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_NUTRITION_STANDARD_FAIL
          };
        }
      }
    ),
    importFile: create.asyncThunk(
      async (formData: FormData, { rejectWithValue }) => {
        try {
          const response = await apiFetcher.post(
            'nutritional-standards/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (err: any) {
          const message = err?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_NUTRITION_STANDARD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.IMPORT_COMBO_FOOD_FAIL
          };
        }
      }
    ),
    getHistory: create.asyncThunk(
      async ({ offset, limit }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('offset', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `history-import/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const query = {
          type: HISTORY_TYPE.NUTRITIONAL_STANDARD
        };
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistory = transformerArray(action.payload.listData);
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistory = [];
          state.paginationHistory = PAGINATION;
        }
      }
    ),
    getFilteredNutritionStandards: create.asyncThunk(
      async (filterPayload?: Record<string, any>) => {
        let offset, limit, rest;
        if (filterPayload) {
          ({ offset, limit, ...rest } = filterPayload);
        } else {
          rest = {};
        }
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('offset', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());
        const url = `nutritional-standards/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiFetcher.post(url, rest);

        const apiData = response.data;

        const listDataMapped = transformerArray(apiData.listData);

        return { listData: listDataMapped, paging: apiData.paging };
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.filteredNutritionalStandards = transformerArray(
            action.payload.listData
          );
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.filteredNutritionalStandards = [];
        }
      }
    ),
    getBreastMilkLevels: create.asyncThunk(
      async (ageRangeId: string) => {
        const response = await apiFetcher.get(
          `breast-milk/list?age-range=${ageRangeId}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBreastMilkLevel = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listBreastMilkLevel = [];
        }
      }
    ),
    downloadTemplateNutritionStandard: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          'template/info?type=NUTRITION_STANDARD'
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateNutritionStandard = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateNutritionStandard = undefined;
        }
      }
    ),

    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),
  selectors: {
    watchLoading: (nutritionStandard) => nutritionStandard.loading,
    watchListAgeNutritionStandard: (nutritionStandard) =>
      nutritionStandard.listAgeNutritionStandard,
    watchListHealthStatusNutritionStandard: (nutritionStandard) =>
      nutritionStandard.listHealthStatusNutritionStandard,
    watchListGenderNutritionStandard: (nutritionStandard) =>
      nutritionStandard.listGenderNutritionStandard,
    watchListPhysicalActivityNutritionStandard: (nutritionStandard) =>
      nutritionStandard.listPhysicalActivityNutritionStandard,
    watchListHealthStatusZCoreNutritionStandard: (nutritionStandard) =>
      nutritionStandard.listHealthStatusZCoreNutritionStandard,
    watchListHistory: (nutritionStandard) => nutritionStandard.listHistory,
    watchListBreastMilkLevel: (nutritionStandard) =>
      nutritionStandard.listBreastMilkLevel,
    watchPaginationHistory: (nutritionStandard) =>
      nutritionStandard.paginationHistory,
    watchFilteredNutritionalStandards: (nutritionStandard) =>
      nutritionStandard.filteredNutritionalStandards,
    watchMessage: (nutritionStandard) => nutritionStandard.message,
    watchNutritionLoading: (nutritionStandard) => nutritionStandard.loading,
    watchNutritionStandardsPagination: (nutritionStandard) =>
      nutritionStandard.pagination,
    watchDownloadTemplateNutritionStandard: (nutritionStandard) =>
      nutritionStandard.downloadTemplateNutritionStandard
  }
});

export const {
  watchLoading,
  watchListAgeNutritionStandard,
  watchListHealthStatusNutritionStandard,
  watchListGenderNutritionStandard,
  watchListPhysicalActivityNutritionStandard,
  watchListHealthStatusZCoreNutritionStandard,
  watchPaginationHistory,
  watchListHistory,
  watchMessage,
  watchFilteredNutritionalStandards,
  watchNutritionLoading,
  watchNutritionStandardsPagination,
  watchListBreastMilkLevel,
  watchDownloadTemplateNutritionStandard
} = nutritionStandardSlice.selectors;

export const {
  getListAgeNutritionStandard,
  getListHealthStatusNutritionStandard,
  getListGenderNutritionStandard,
  getListPhysicalActivityNutritionStandard,
  getListHealthStatusZCoreNutritionStandard,
  importFile,
  getHistory,
  getFilteredNutritionStandards,
  editNutritionStandard,
  createNutritionStandard,
  deleteNutritionStandard,
  resetMessages,
  getBreastMilkLevels,
  downloadTemplateNutritionStandard
} = nutritionStandardSlice.actions;
